import React, { useCallback, useMemo } from "react";
import { Image } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import {
	GenericSwitch,
	OverflowTooltip,
	Table,
	TableContainer,
	TableTooltip,
} from "components";
import { getHighRiskIcon, getSplitProfileFriendlyId } from "mappers";
import {
	ColumnTypes,
	ScenarioTypes,
	SplitProfileTableProps,
	SplitProfileTypes,
	TableCellProp,
} from "types";

import SplitProfileTableActions from "./splitProfileTableActions";

import styles from "./splitProfileTable.module.scss";

const SplitProfileTable = ({
	data = [],
	sortBy = [],
	count = 0,
	loading = false,
	pageIndex = 0,
	setPaging,
	setSorting,
	tableHeight = "315px",
	agentId = "",
	setStatus,
	setDefault,
	isEditable,
}: SplitProfileTableProps) => {
	const history = useHistory();

	const handlePageChange = page => {
		setPaging(page);
	};

	const handleSortChange = sort => {
		setSorting(sort);
	};

	const onRowClick = useCallback(
		({ original }) => {
			history.push(`${history.location.pathname}/view/${original.id}`);
		},
		[history],
	);

	const meta = useMemo(
		() => ({
			loading,
			totalItems: count,
		}),
		[loading, count],
	);

	const handleStatusChange = useCallback(
		splitProfile => {
			setStatus({
				agentId,
				splitProfileId: splitProfile.id,
				status: !splitProfile.is_available,
			});
		},
		[agentId, setStatus],
	);

	const handleDefaultChange = useCallback(
		splitProfile => {
			setDefault({
				agentId,
				splitProfileId: splitProfile.id,
				isDefault: !splitProfile.is_default,
			});
		},
		[setDefault, agentId],
	);

	const columns: ColumnTypes = useMemo(
		() => [
			{
				Header: "ID",
				accessor: "id",
				classNameHeader: `${styles.cellWithIconHeader}`,
				Cell: ({
					row: { original },
				}: TableCellProp<SplitProfileTypes & { id: string }>) => {
					return (
						<div
							className="cellWithIcon cut-text-laptop"
							style={{
								paddingLeft: "50px",
							}}
						>
							<TableTooltip
								text={`${
									!original?.is_high_risk ? "No" : ""
								} High Risk `}
								placement="top"
							>
								<Image
									src={getHighRiskIcon(
										original?.is_high_risk,
									)}
									className={"iconStatus"}
								/>
							</TableTooltip>
							<OverflowTooltip
								onlyOnLaptop
								title={getSplitProfileFriendlyId(original)}
							>
								{getSplitProfileFriendlyId(original)}
							</OverflowTooltip>
						</div>
					);
				},
			},
			{
				Header: "Residual Split Name",
				accessor: "name",
				Cell: ({
					cell,
				}: TableCellProp<SplitProfileTypes & { id: string }>) => {
					return (
						<OverflowTooltip onlyOnLaptop title={cell?.value}>
							{cell?.value}
						</OverflowTooltip>
					);
				},
			},
			{
				Header: "Scenario",
				accessor: "scenario",
				Cell: ({
					cell,
				}: TableCellProp<SplitProfileTypes & { id: string }>) => {
					const scenario = ScenarioTypes.getByValue(cell?.value);
					return (
						<OverflowTooltip
							onlyOnLaptop
							title={scenario?.displayName}
						>
							{scenario?.displayName}
						</OverflowTooltip>
					);
				},
			},
			{
				Header: "Default",
				accessor: "default",
				disableSortBy: true,
				Cell: ({
					row: { original },
				}: TableCellProp<SplitProfileTypes & { id: string }>) => {
					const isDefault = original?.is_default === true;
					const isAvailable = original?.is_available === true;
					const isHighRisk = original?.is_high_risk === true;

					return (
						<div className="d-flex">
							{isEditable ? (
								<GenericSwitch
									name={`isDefault_${original.id}`}
									id={`isDefault_${original.id}`}
									enabled={isDefault}
									onChange={_ =>
										handleDefaultChange(original)
									}
									isDisabled={!isAvailable || isHighRisk}
								/>
							) : isDefault ? (
								"Is Default"
							) : (
								""
							)}
						</div>
					);
				},
			},
			{
				Header: "Status",
				accessor: "status",
				Cell: ({
					row: { original },
				}: TableCellProp<SplitProfileTypes & { id: string }>) => {
					const isAvailable = original?.is_available === true;
					return (
						<div className="d-flex">
							{isEditable && (
								<GenericSwitch
									name={`profile_${original.id}`}
									id={`profile_${original.id}`}
									enabled={isAvailable}
									onChange={_ => handleStatusChange(original)}
								/>
							)}
							<OverflowTooltip
								onlyOnLaptop
								title={
									isAvailable ? "Available" : "Unavailable"
								}
								className="ml-3"
							>
								{isAvailable ? "Available" : "Unavailable"}
							</OverflowTooltip>
						</div>
					);
				},
			},
			{
				Header: "Actions",
				accessor: "",
				disableSortBy: true,
				classNameCell: `${styles.actionsCell}`,
				classNameHeader: `${styles.actionsCellHeader}`,
				Cell: ({
					row,
				}: TableCellProp<SplitProfileTypes & { id: string }>) => {
					return (
						<SplitProfileTableActions
							row={row}
							isEditable={isEditable}
						/>
					);
				},
			},
		],
		[handleStatusChange, handleDefaultChange, isEditable],
	);

	return (
		<TableContainer tableHeight={tableHeight}>
			<Table
				columns={columns}
				data={data}
				handleSortChange={handleSortChange}
				handlePageChange={handlePageChange}
				onRowClick={onRowClick}
				meta={meta}
				initialState={{
					sortBy: sortBy,
					pageIndex,
				}}
				stickyFooter
				stickyHeader
				compact
			/>
		</TableContainer>
	);
};

export default SplitProfileTable;
